/**
 * The active lens, per browser.
 *
 * Contract shared with the Account page (pages/profile/preferences.js): the id
 * lives in localStorage under LENS_STORAGE_KEY as a plain string, and whoever
 * changes it dispatches LENS_EVENT on window.
 *
 * Which lens applies:
 *   - a stored id that names a lens this user can see → that lens;
 *   - a stored 'all' → Everything (the full sidebar);
 *   - nothing stored, or an id that no longer resolves → the role lens whose
 *     `default_for_roles` match the user's role ("chosen for your role"),
 *     else Everything.
 * Until the manifests load, or if they cannot be loaded, it is Everything.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLenses } from '../../config/lenses/useLenses';
import { resolveLenses, roleDefaultLens } from '../../config/lenses/derive';
import { readNavAccess } from './lensAccess';

export const LENS_STORAGE_KEY = 'saf3ai.lens';
export const LENS_EVENT = 'saf3ai:lens-changed';
export const DEFAULT_LENS_ID = 'all';

/** Everything: no lens, the full sidebar. */
export const EVERYTHING = Object.freeze({
  id: 'all',
  kind: 'all',
  label: null,
  abbr: 'ALL',
  color: '#8B95A3',
  composes: [],
  groups: [],
  sections: [],
  home: null,
  visible: true,
});

const ID = /^[a-z][a-z0-9-]*$/;
let memoryLensId = null;

const parseId = (raw) => {
  if (raw == null) return null;
  let value = String(raw).trim();
  // Tolerate a JSON-encoded string ("\"ai-soc\"") from writers that stringify.
  if (value.startsWith('"') && value.endsWith('"')) {
    try { value = JSON.parse(value); } catch { /* keep as-is */ }
  }
  return ID.test(value) ? value : null;
};

/** The id the user chose, or null when they have not chosen (role default applies). */
export const readStoredLensId = () => {
  try {
    return parseId(window.localStorage.getItem(LENS_STORAGE_KEY));
  } catch {
    return memoryLensId;
  }
};

/**
 * Persist and announce a lens choice. `null` forgets the choice, so the role
 * default applies again. Returns the id applied ('' for null), or null if malformed.
 */
export const writeLensId = (id) => {
  const next = id === null ? '' : parseId(id);
  if (next === null) return null;
  memoryLensId = next || null;
  try {
    if (next) window.localStorage.setItem(LENS_STORAGE_KEY, next);
    else window.localStorage.removeItem(LENS_STORAGE_KEY);
  } catch {
    // Storage blocked — the in-memory value carries the session.
  }
  try {
    window.dispatchEvent(new CustomEvent(LENS_EVENT, { detail: { lensId: next } }));
  } catch {
    // ignore
  }
  return next;
};

/** Which lens applies, given the resolved set and the stored choice. */
export const pickLens = (resolved, storedId, access) => {
  if (storedId === DEFAULT_LENS_ID) return { lens: EVERYTHING, chosenForRole: false };
  const stored = storedId ? resolved.byId.get(storedId) : null;
  if (stored && stored.visible) return { lens: stored, chosenForRole: false };
  const byRole = roleDefaultLens(resolved, access);
  return byRole ? { lens: byRole, chosenForRole: true } : { lens: EVERYTHING, chosenForRole: false };
};

/**
 * useLens() → { lens, lensId, chosenForRole, setLens(id), useCases, roles, ready }
 * useCases / roles: the lenses this user can see, in switcher order.
 */
export default function useLens() {
  const { status, body } = useLenses();
  const [storedId, setStoredId] = useState(readStoredLensId);
  // Bumped when the signed-in user changes, so access is read again.
  const [userVersion, setUserVersion] = useState(0);

  useEffect(() => {
    const onChange = () => setStoredId(readStoredLensId());
    const onStorage = (e) => {
      if (!e || e.key === null || e.key === LENS_STORAGE_KEY) setStoredId(readStoredLensId());
      if (!e || e.key === null || e.key === 'user') setUserVersion((v) => v + 1);
    };
    window.addEventListener(LENS_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(LENS_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const access = useMemo(() => readNavAccess(), [userVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const resolved = useMemo(
    () => (status === 'ready' ? resolveLenses(body, access) : null),
    [status, body, access],
  );
  const picked = resolved ? pickLens(resolved, storedId, access) : { lens: EVERYTHING, chosenForRole: false };

  const setLens = useCallback((id) => {
    const applied = writeLensId(id);
    if (applied !== null) setStoredId(applied || null);
    return applied;
  }, []);

  return {
    lens: picked.lens,
    lensId: picked.lens.id,
    chosenForRole: picked.chosenForRole,
    setLens,
    useCases: resolved ? resolved.useCases.filter((l) => l.visible) : [],
    roles: resolved ? resolved.roles.filter((l) => l.visible) : [],
    ready: Boolean(resolved),
  };
}
